import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Process } from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { combineLatest, Observable } from 'rxjs';
import { tap, map, take, takeWhile } from 'rxjs/operators';
import { UserSetting } from '../store';
import * as fromUserInfo from '../../user-info';
import * as fromUserSetting from '../store';
import * as fromCompanySetting from '../../company-setting';
import * as fromLookups from '../../lookups';
import * as fromShared from '../../shared';

@Component({
    selector: 'ef-user-setting-feature',
    templateUrl: './user-setting.feature.html',
    styleUrls: ['./user-setting.feature.scss'],
    standalone: false
})
export class UserSettingFeatureComponent implements OnInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    settings$: Observable<Array<fromUserSetting.UserSetting>>;
    categories$: Observable<{ CatID; Description }[]>;
    colors$: Observable<fromLookups.Color[]>;
    timeZones$: Observable<Array<Partial<fromLookups.ITimeZone>>>;
    allTimeZones$: Observable<Array<Partial<fromLookups.ITimeZone>>>;
    private active = true;
    constructor(
        private log: LoggingService,
        private store: Store<fromUserSetting.State>
    ) {}

    ngOnInit() {
        this.store.dispatch(new fromLookups.LoadUserTimeZones());
        this.store.dispatch(new fromLookups.LoadColors());
        this.setupObservables();
        this.setupSubscriptions();
        this.removeUserAIHelpButtonIfCompanyDisabled();
        // this.store.dispatch(new fromLookups.LoadPayrollFrequency());
    }
    ngOnDestroy() {
        this.active = false;
        //this.removeUserAIHelpButtonIfCompanyDisabled();
    }
    private removeUserAIHelpButtonIfCompanyDisabled() {
        this.store
            .pipe(
                select(
                    fromCompanySetting.getSettingBooleanValue(
                        fromCompanySetting.CompanySettingEnum.ShowAIHelpButton
                    )
                ),
                take(1)
            )
            .subscribe(showAIHelpButton => {
                if (!showAIHelpButton) {
                    this.store.dispatch(
                        fromUserSetting.deleteUserAIHelpButton()
                    );
                }
            });
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.allTimeZones$ = this.store.pipe(select(fromLookups.getTimeZones));
        this.timeZones$ = combineLatest([
            this.store.pipe(select(fromLookups.getUserTimeZones)),
            this.store.pipe(select(fromLookups.getTimeZones))
        ]).pipe(
            map(([userTimeZones, allTimeZones]) => {
                //augment the userTimeZones with the data from allTimeZones joining on StandardName
                return userTimeZones.map(userTimeZone => {
                    const allTimeZone = allTimeZones.find(
                        x => x.StandardName === userTimeZone.StandardName
                    );
                    return {
                        ...userTimeZone,
                        ...allTimeZone
                    };
                });
            })
        );
        this.colors$ = this.store.pipe(select(fromLookups.getColors));
        this.settings$ = this.store.pipe(
            select(fromUserSetting.selectAll),
            tap(settings =>
                this.log.verbose({
                    type: 'UserSettingFeatureComponent:settings$',
                    settings
                })
            ),
            map(x =>
                x
                .filter(x=>x.UserDefined)
                .map(y => ({
                    ...y,
                    CtrlType: y.CtrlType.toLocaleLowerCase(),
                    EmpSettingValue:
                        y.CtrlType.toLocaleLowerCase() === 'checkbox'
                            ? y.EmpSettingValue === '1'
                            : y.EmpSettingValue
                }))
            )
        );
        this.categories$ = this.store.pipe(
            select(fromUserSetting.getCategories)
        );
    }

    setupSubscriptions() {
        this.store
            .pipe(
                select(fromUserInfo.getUserInfo),
                takeWhile(() => this.active)
            )
            .subscribe(userInfo => {
                this.store.dispatch(
                    fromUserSetting.getUserSettings({
                        EmployeeListID: userInfo.EmployeeListID
                    })
                );
            });
        this.store
            .pipe(
                select(
                    fromUserSetting.getSetting(
                        fromUserSetting.UserSettingEmpSettingType
                            .UserAIHelpButton
                    )
                ),
                takeWhile(() => this.active)
            )
            .subscribe(userAIHelpButton => {
                if (userAIHelpButton) {
                    this.removeUserAIHelpButtonIfCompanyDisabled();
                }
            });
    }
    onSave(setting: UserSetting) {
        this.log.verbose({
            type: 'UserSettingFeatureComponent:onSave',
            setting
        });
        this.store.dispatch(
            fromUserSetting.putUserSettings({ settings: [setting] })
        );
    }
}
